import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
    MatTabChangeEvent,
    MatTabLabel,
    MatTabsModule,
} from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
} from 'ag-grid-community';
import cloneDeep from 'lodash.clonedeep';
import {
    debounceTime,
    fromEventPattern,
    map,
    merge,
    Observable,
    of,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs';
import { ResourceDataKeys } from '../../../../core/constants/resource-data-keys';
import { nameOf } from '../../../../core/functions/string-helpers';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../core/models/form-group-type';
import { GridTooltipHelperMessages } from '../../../../core/models/grid/constants/grid-tooltip-messages';
import { FormInputCellRendererParams } from '../../../../core/models/grid/form-input-cell-renderer-params';
import { FormSelectCellRendererParams } from '../../../../core/models/grid/form-select-cell-renderer-params';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { SingleActionCellRendererParams } from '../../../../core/models/grid/single-action-cell-renderer-params';
import { ActionCode } from '../../../../core/models/resources/action-code';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { ApplicationPageBaseComponent } from '../../../../shared/components/fcc-601/application-page-base/application-page-base.component';
import { ButtonCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/button-cell-renderer/button-cell-renderer.component';
import { ButtonCellRendererParams } from '../../../../shared/components/grid/cell-renderers/button-cell-renderer/models/button-cell-renderer-params';
import { SingleActionCellRendererBaseComponent } from '../../../../shared/components/grid/cell-renderers/single-action-cell-renderer-base/single-action-cell-renderer-base.component';
import { GridToolbarComponent } from '../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { CheckboxHeaderRendererComponent } from '../../../../shared/components/grid/header-renderers/checkbox-header-renderer/checkbox-header-renderer.component';
import { FrequencyMaskConfig } from '../../shared/constants/frequency-mask-config';
import { SchedulesDAndHMaskConfig } from '../../shared/constants/schedules-d-and-h-mask-config';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { ApplicationStateService } from '../../shared/services/application-state/application-state.service';
import { ApplicationService } from '../../shared/services/application/application.service';
import { ApplicationPageFacadeService } from '../shared/services/application-page-facade/application-page-facade.service';
import { DirectEntryService } from '../shared/services/direct-entry/direct-entry.service';
import { SchedulesDAndHSharedFormService } from '../shared/services/schedules-d-and-h-shared-form/schedules-d-and-h-shared-form.service';
import { EmissionDesignatorCellRendererComponent } from './components/emission-designator-cell-renderer/emission-designator-cell-renderer.component';
import { EmissionDesignatorCellRendererParams } from './components/emission-designator-cell-renderer/models/emission-designator-cell-renderer-params';
import { EmissionDesignatorOperationComponent } from './components/emission-designator-operation/emission-designator-operation.component';
import { ExistingLicenseEmissionDesignatorOperationComponent } from './components/existing-license-emission-designator-operation/existing-license-emission-designator-operation.component';
import { CheckSafeHarborRequest } from './components/safe-harbor-results/models/check-safe-harbor-request';
import { CheckSafeHarborResponse } from './components/safe-harbor-results/models/check-safe-harbor-response';
import { SafeHarborResultsModalData } from './components/safe-harbor-results/models/safe-harbor-results-modal-data';
import { SafeHarborResultsComponent } from './components/safe-harbor-results/safe-harbor-results.component';
import { ScheduleDBaseComponent } from './components/schedule-d-base/schedule-d-base.component';
import { AntennaGridHelperMessages } from './constants/antenna-grid-helper-messages';
import { AntennaValidationMessages } from './constants/antenna-validation-messages';
import { CheckSafeHarborMessages } from './constants/check-safe-harbor-messages';
import { FrequencyGridHelperMessages } from './constants/frequency-grid-helper-messages';
import { FrequencyValidationMessages } from './constants/frequency-validation-messages';
import { ScheduleDValidationMessages } from './constants/schedule-d-validation-messages';
import { AntennaFormModel } from './models/antenna-form-model';
import { EmissionDesignator } from './models/emission-designator';
import { EmissionDesignatorOperation } from './models/emission-designator-operation';
import { ExistingLicenseEmissionDesignatorOperation } from './models/existing-license-emission-designator-operation';
import { FrequencyFormModel } from './models/frequency-form-model';
import {
    FrequencyTypeCode,
    FrequencyTypeToFrequencyPair,
} from './models/frequency-type-codes';
import { AntennaMaskConfig } from './models/mask-config/antenna-mask-config';
import { ScheduleDFormModel } from './models/schedule-d-form-model';
import { SchedulesDAndHBase } from './models/schedules-d-and-h-base';
import { SchedulesDAndHFormModel } from './models/schedules-d-and-h-form-model';
import { SchedulesDAndHGridType } from './models/schedules-d-and-h-grid-type';
import { SchedulesDAndHRequest } from './models/schedules-d-and-h-request';
import { SchedulesDAndHResponse } from './models/schedules-d-and-h-response';
import { AntennaFormService } from './services/antenna-form/antenna-form.service';
import { EmissionDesignatorFormService } from './services/emission-designator-form/emission-designator-form.service';
import { FrequencyFormService } from './services/frequency-form/frequency-form.service';
import { PopulateScheduleDAsrDataService } from './services/schedule-d-form/populate-schedule-d-asr-data.service';
import { ScheduleDFormService } from './services/schedule-d-form/schedule-d-form.service';
import { SchedulesDAndHFormService } from './services/schedules-d-and-h-form/schedules-d-and-h-form.service';
import { SchedulesDAndHGridApiMap } from './types/schedules-d-and-h-grid-api-map';
import { SchedulesDAndHRowDataMap } from './types/schedules-d-and-h-row-data-map';
import { FrequencyFormattingService } from '../../../../core/services/frequency-formatting/frequency-formatting.service';

@Component({
    selector: 'app-schedules-d-and-h',
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        ScheduleDBaseComponent,
        MatInputModule,
        MatIconModule,
        AgGridAngular,
        MatExpansionModule,
        MatTabsModule,
        MatTabLabel,
        MatTooltipModule,
        MatButtonModule,
        GridToolbarComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './schedules-d-and-h.component.html',
})
export class SchedulesDAndHComponent
    extends ApplicationPageBaseComponent<
        SchedulesDAndHRequest,
        SchedulesDAndHResponse,
        SchedulesDAndHFormModel
    >
    implements OnInit, OnDestroy, BlockNavigationIfChange
{
    @ViewChild(ScheduleDBaseComponent)
    scheduleDBaseComponent!: ScheduleDBaseComponent;

    checkSafeHarborMessages = CheckSafeHarborMessages;
    schedulesDAndHGridType = SchedulesDAndHGridType;
    scheduleDValidationMessages = ScheduleDValidationMessages;
    antennaValidationMessages = AntennaValidationMessages;
    frequencyValidationMessages = FrequencyValidationMessages;
    antennaGridMessages = AntennaGridHelperMessages;
    frequencyGridMessages = FrequencyGridHelperMessages;
    gridTooltipMessages = GridTooltipHelperMessages;

    addScheduleDReady: boolean = false;

    schedulesDAndHResponse: SchedulesDAndHResponse | undefined;

    rowData: SchedulesDAndHRowDataMap = {
        [SchedulesDAndHGridType.scheduleDs]: [],
        [SchedulesDAndHGridType.antennas]: [],
        [SchedulesDAndHGridType.frequencies]: [],
    };

    gridApis: SchedulesDAndHGridApiMap = {} as SchedulesDAndHGridApiMap;

    /**
     * Tracks the latest known validity for each Schedule D row.
     *
     * AG Grid rowClassRules must be synchronous, so we update this map with the
     * async validation result and then trigger a redraw. The row class rule reads
     * this map and returns a boolean immediately.
     */
    private readonly scheduleDValidationState = new Map<string, boolean>();

    scheduleDGridToolbarOptions: GridToolbarOptions = {
        quickFilter: false,
        exportCsv: true,
        exportCsvOptions: {
            columnKeys: [
                nameOf<ScheduleDFormModel>('actionCode'),
                nameOf<ScheduleDFormModel>('number'),
                nameOf<ScheduleDFormModel>('locationTypeCode'),
                nameOf<ScheduleDFormModel>('streetAddress'),
                nameOf<ScheduleDFormModel>('city'),
                nameOf<ScheduleDFormModel>('county'),
                nameOf<ScheduleDFormModel>('state'),
            ],
            fileName: () =>
                GridHelper.getExportFileName(
                    'Locations',
                    this.applicationState?.salmonLogId,
                ),
        },
        refreshDataForExport: () =>
            this.updateGrid(SchedulesDAndHGridType.scheduleDs),
    };

    scheduleDColumnDefs: ColDef[] = [
        GridHelper.deleteRowColDef,
        GridHelper.actionCodeColDef,
        {
            headerName: 'Location #',
            field: nameOf<ScheduleDFormModel>('number'),
            maxWidth: 120,
            ...GridHelper.disableCellFocusBorderColDef,
            cellRenderer: SingleActionCellRendererBaseComponent,
            cellRendererParams: {
                action: (params: SingleActionCellRendererParams) =>
                    this.loadScheduleDIntoForm(params),
            } as SingleActionCellRendererParams,
            filter: false,
        },
        {
            headerName: 'Description',
            field: nameOf<ScheduleDFormModel>('locationTypeCode'),
            maxWidth: 120,
            filter: false,
        },
        {
            headerName: 'Street Address',
            field: nameOf<ScheduleDFormModel>('streetAddress'),
            minWidth: 280,
            filter: false,
        },
        {
            headerName: 'City or Town',
            field: nameOf<ScheduleDFormModel>('city'),
            minWidth: 200,
            filter: false,
        },
        {
            headerName: 'County/Borough/Parish',
            field: nameOf<ScheduleDFormModel>('county'),
            minWidth: 205,
            filter: false,
        },
        {
            headerName: 'State',
            field: nameOf<ScheduleDFormModel>('state'),
            maxWidth: 120,
            filter: false,
        },
    ];

    scheduleDGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        ...GridHelper.getErrorHighlightingGridOptions<ScheduleDFormModel>(
            // Row class rules must be synchronous. Instead of returning an Observable,
            // read the latest boolean from the in-memory state map that is updated
            // after the async validation finishes.
            (data: ScheduleDFormModel) => {
                const key = this.getScheduleDRowKeyForValidationMap(data);

                // If the key is not found in the validation state map, default to true (valid).
                return this.scheduleDValidationState.get(key) ?? true;
            },
            // Is being edited function
            (data: ScheduleDFormModel) => data.isBeingEdited === true,
        ),
        columnDefs: this.scheduleDColumnDefs,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
        getRowId: GridHelper.defaultGetRowId,
    };

    antennasGridToolbarOptions: GridToolbarOptions = {
        quickFilter: false,
        exportCsv: true,
        exportCsvOptions: {
            columnKeys: [
                nameOf<AntennaFormModel>('actionCode'),
                nameOf<AntennaFormModel>('number'),
                nameOf<AntennaFormModel>('locationNumber'),
                nameOf<AntennaFormModel>('isOmniDirectional'),
                nameOf<AntennaFormModel>('heightAboveAverageTerrain'),
                nameOf<AntennaFormModel>('heightToTip'),
                nameOf<AntennaFormModel>('azimuth'),
                nameOf<AntennaFormModel>('beamwidth'),
                nameOf<AntennaFormModel>('polarizationCode'),
                nameOf<AntennaFormModel>('gain'),
            ],
            fileName: () =>
                GridHelper.getExportFileName(
                    'Antennas',
                    this.applicationState?.salmonLogId,
                ),
        },
        refreshDataForExport: () =>
            this.updateGrid(SchedulesDAndHGridType.antennas),
    };

    antennaColumnDefs: ColDef[] = [
        GridHelper.deleteRowColDef,
        GridHelper.actionCodeColDef,
        {
            headerName: 'Location #',
            field: nameOf<AntennaFormModel>('locationNumber'),
            minWidth: 145,
            maxWidth: 145,
            filter: false,
            ...GridHelper.defaultFormSelectCellRendererColDef,
            cellRendererParams: {
                title: 'Location #',
                dropdownDataFunction: (_) =>
                    this.formService.getScheduleDNumberDropdownOptions(
                        this.scheduleDs(),
                    ),
                classList: 'text-center',
            } as FormSelectCellRendererParams,
            headerTooltip:
                this.antennaGridMessages.locationNumberTooltipDescription,
        },
        {
            headerName: 'Antenna #',
            field: nameOf<AntennaFormModel>('number'),
            valueFormatter: (params) => {
                return params.value ? `A${params.value.toString()}` : '';
            },
            maxWidth: 120,
            filter: false,
            headerTooltip:
                this.antennaGridMessages.antennaNumberTooltipDescription,
        },
        {
            headerName: 'Omni?',
            field: nameOf<AntennaFormModel>('isOmniDirectional'),
            minWidth: 130,
            maxWidth: 200,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormSelectCellRendererColDef,
            cellRendererParams: {
                title: 'State',
                dropdownDataFunction: (_) =>
                    this.dropdownService.getYesNoOptions(),
                classList: 'text-center',
                onSelectionChange: (control: FormControl) => {
                    this.handleAntennaFocusOut(control);
                },
            } as FormSelectCellRendererParams,
        },
        {
            headerName: 'AAT (meters)',
            field: nameOf<AntennaFormModel>('heightAboveAverageTerrain'),
            wrapHeaderText: true,
            minWidth: 145,
            maxWidth: 165,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'AAT (meters)',
                classList: 'text-center',
                imask: SchedulesDAndHMaskConfig.heightOfStructure,
                onFocusout: (control: FormControl) => {
                    this.handleAntennaFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip: this.antennaGridMessages.aatTooltipDescription,
        },
        {
            headerName: 'Antenna Height',
            field: nameOf<AntennaFormModel>('heightToTip'),
            minWidth: 180,
            maxWidth: 300,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Antenna Height',
                classList: 'text-center',
                imask: SchedulesDAndHMaskConfig.heightOfStructure,
                onFocusout: (control: FormControl) => {
                    this.handleAntennaFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.antennaGridMessages.antennaHeightTooltipDescription,
        },
        {
            headerName: 'Azimuth (degrees)',
            field: nameOf<AntennaFormModel>('azimuth'),
            wrapHeaderText: true,
            maxWidth: 135,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Azimuth (degrees)',
                classList: 'text-center',
                imask: AntennaMaskConfig.azimuth,
                onFocusout: (control: FormControl) => {
                    this.handleAntennaFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip: this.antennaGridMessages.azimuthTooltipDescription,
        },
        {
            headerName: 'Beamwidth (degrees)',
            field: nameOf<AntennaFormModel>('beamwidth'),
            wrapHeaderText: true,
            maxWidth: 135,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Beamwidth (degrees)',
                classList: 'text-center',
                imask: AntennaMaskConfig.beamwidth,
                onFocusout: (control: FormControl) => {
                    this.handleAntennaFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip: this.antennaGridMessages.beamWidthTooltipDescription,
        },
        {
            headerName: 'Polarization',
            field: nameOf<AntennaFormModel>('polarizationCode'),
            minWidth: 200,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormSelectCellRendererColDef,
            cellRendererParams: {
                title: 'Polarization',
                referenceDataType: ResourceDataKeys.polarizationCodes,
                classList: 'text-center',
                onSelectionChange: (control: FormControl) => {
                    this.handleAntennaFocusOut(control);
                },
            } as FormSelectCellRendererParams,
            headerTooltip:
                this.antennaGridMessages.polarizationTooltipDescription,
        },
        {
            headerName: 'Gain (dB)',
            field: nameOf<AntennaFormModel>('gain'),
            minWidth: 140,
            maxWidth: 140,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Gain (dB)',
                classList: 'text-center',
                imask: AntennaMaskConfig.gain,
                onFocusout: (control: FormControl) => {
                    this.handleAntennaFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip: this.antennaGridMessages.gainTooltipDescription,
        },
    ];

    antennaGridOptions: GridOptions = {
        ...GridHelper.defaultFormFieldGridOptions,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
        columnDefs: this.antennaColumnDefs,
        getRowId: GridHelper.defaultGetRowId,
    };

    frequenciesGridToolbarOptions: GridToolbarOptions = {
        quickFilter: false,
        exportCsv: true,
        exportCsvOptions: {
            columnKeys: [
                nameOf<FrequencyFormModel>('actionCode'),
                nameOf<FrequencyFormModel>('locationNumber'),
                nameOf<FrequencyFormModel>('antennaNumber'),
                nameOf<FrequencyFormModel>('emissionDesignatorsSelected'),
                nameOf<FrequencyFormModel>('frequencyLow'),
                nameOf<FrequencyFormModel>('frequencyHigh'),
                nameOf<FrequencyFormModel>('numberOfFrequenciesNeeded'),
                nameOf<FrequencyFormModel>('stationClassCode'),
                nameOf<FrequencyFormModel>('numberOfUnits'),
                nameOf<FrequencyFormModel>('powerOutput'),
                nameOf<FrequencyFormModel>('powerErp'),
                nameOf<FrequencyFormModel>('numberOfPagingReceivers'),
            ],
            fileName: () =>
                GridHelper.getExportFileName(
                    'Frequencies',
                    this.applicationState?.salmonLogId,
                ),
        },
        refreshDataForExport: () =>
            this.updateGrid(SchedulesDAndHGridType.frequencies),
    };

    frequencyColumnDefs: ColDef[] = [
        GridHelper.deleteRowColDef,
        {
            headerName: 'Copy',
            maxWidth: 96,
            filter: false,
            sortable: false,
            hide: this.isReadOnly(),
            cellRenderer: ButtonCellRendererComponent,
            cellRendererParams: {
                classList: '!rounded-full',
                matIconIdentifier: 'content_copy',
                iconClassList: '!m-0',
                clickFunction: (params: ButtonCellRendererParams) =>
                    this.copyFrequencyToGrid(params),
            },
        },
        GridHelper.actionCodeColDef,
        {
            headerName: 'Location #',
            field: nameOf<FrequencyFormModel>('locationNumber'),
            minWidth: 145,
            maxWidth: 145,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormSelectCellRendererColDef,
            cellRendererParams: {
                title: 'Location #',
                dropdownDataFunction: (_) =>
                    // Cannot use `getScheduleDNumberDropdownOptions` here because there may not be an antenna for all locations
                    this.formService.getAntennaLocationNumberDropdownOptions(
                        this.antennas(),
                    ),
                classList: 'text-center',
                onSelectionChange: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormSelectCellRendererParams,
            headerTooltip:
                this.frequencyGridMessages.locationNumberTooltipDescription,
        },
        {
            headerName: 'Antenna #',
            field: nameOf<FrequencyFormModel>('antennaNumber'),
            maxWidth: 125,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormSelectCellRendererColDef,
            cellRendererParams: {
                title: 'Antenna #',
                dropdownDataFunction: (params: ICellRendererParams) =>
                    this.formService.getAntennaNumberDropdownOptions(
                        this.antennas(),
                        params,
                    ),
                classList: 'text-center',
                onSelectionChange: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormSelectCellRendererParams,
            headerTooltip:
                this.frequencyGridMessages.antennaNumberTooltipDescription,
        },
        {
            headerName: 'Frequency Band',
            field: nameOf<FrequencyFormModel>('frequencyTypeCode'),
            minWidth: 140,
            maxWidth: 170,
            wrapHeaderText: true,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormSelectCellRendererColDef,
            cellRendererParams: {
                title: 'Frequency Band',
                referenceDataType: ResourceDataKeys.frequencyType,
                dropdownDataOptions: {
                    excludeDescriptions: true,
                    allowEmpty: true,
                    reverseOrder: true,
                },
                classList: 'text-center',
                onSelectionChange: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormSelectCellRendererParams,
        },
        {
            headerName: 'Frequency Low',
            field: nameOf<FrequencyFormModel>('frequencyLow'),
            minWidth: 145,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Frequency Low',
                classList: 'text-center',
                imask: FrequencyMaskConfig.frequency,
                onFocusout: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.frequencyGridMessages.frequencyTooltipDescription,
        },
        {
            headerName: 'Frequency High',
            field: nameOf<FrequencyFormModel>('frequencyHigh'),
            minWidth: 145,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Frequency High',
                imask: FrequencyMaskConfig.frequency,
                onFocusout: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.frequencyGridMessages.frequencyTooltipDescription,
        },
        {
            headerName: 'Emission Designators',
            wrapHeaderText: true,
            field: nameOf<FrequencyFormModel>('emissionDesignatorsSelected'),
            minWidth: 235,
            maxWidth: 235,
            filter: false,
            sortable: false,
            cellRenderer: EmissionDesignatorCellRendererComponent,
            ...GridHelper.disableCellFocusBorderColDef,
            cellClass: (params) => {
                return params.data.emissionDesignators.length === 0
                    ? 'bg-error-50'
                    : null;
            },
            cellRendererParams: {
                isReadOnly: this.isReadOnly(),
                getEmissionDesignatorsFunction: (params: ICellRendererParams) =>
                    this.frequencyFormService.getEmissionDesignators(
                        this.frequencies(),
                        params,
                    ),
                openEmissionDesignatorOperation: (
                    params: EmissionDesignatorCellRendererParams,
                ) =>
                    this.applicationPageFacadeService.applicationStateService.IsExistingLicenseApplication()
                        ? this.openSingleRowEmissionDesignatorOperationExistingLicense(
                              params,
                          )
                        : this.openSingleRowEmissionDesignatorOperation(params),
            } as EmissionDesignatorCellRendererParams,
            headerComponentParams: {
                disabled: this.isReadOnly(),
                checkboxFunction: (event: MatCheckboxChange) => {
                    this.markEmissionDesignatorsSelected(event.checked);
                },
            },
            headerTooltip:
                this.frequencyGridMessages.emissionDesignatorTooltipDescription,
            valueGetter: (params) => {
                const emissionDesignators: EmissionDesignator[] =
                    params.data.emissionDesignators;
                if (emissionDesignators && emissionDesignators.length > 0) {
                    return emissionDesignators.map((ed) => ed.code).join(', ');
                } else {
                    return '';
                }
            },
        },
        {
            headerName: '# of Freq Needed',
            field: nameOf<FrequencyFormModel>('numberOfFrequenciesNeeded'),
            wrapHeaderText: true,
            maxWidth: 120,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Number of Frequencies Needed',
                type: 'number',
                classList: 'text-center',
                min: 0,
                onFocusout: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormInputCellRendererParams,
        },
        {
            headerName: 'Station Class',
            field: nameOf<FrequencyFormModel>('stationClassCode'),
            minWidth: 360,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormSelectCellRendererColDef,
            cellRendererParams: {
                title: 'Station Class',
                referenceDataType: ResourceDataKeys.stationClass,
                classList: 'text-center',
                onSelectionChange: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormSelectCellRendererParams,
            headerTooltip:
                this.frequencyGridMessages.stationClassTooltipDescription,
        },
        {
            headerName: '# of Units',
            field: nameOf<FrequencyFormModel>('numberOfUnits'),
            maxWidth: 115,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Number of Units',
                type: 'number',
                classList: 'text-center',
                min: 0,
                onFocusout: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.frequencyGridMessages.numberOfUnitsTooltipDescription,
        },
        {
            headerName: 'Output Power (Watts)',
            field: nameOf<FrequencyFormModel>('powerOutput'),
            wrapHeaderText: true,
            minWidth: 145,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Output Power (Watts)',
                classList: 'text-center',
                imask: FrequencyMaskConfig.power,
                onFocusout: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.frequencyGridMessages.outputPowerTooltipDescription,
        },
        {
            headerName: 'ERP (Watts)',
            field: nameOf<FrequencyFormModel>('powerErp'),
            maxWidth: 150,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'ERP (Watts)',
                classList: 'text-center',
                imask: FrequencyMaskConfig.power,
                onFocusout: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip: this.frequencyGridMessages.erpTooltipDescription,
        },
        {
            headerName: '# of Paging Receivers',
            field: nameOf<FrequencyFormModel>('numberOfPagingReceivers'),
            wrapHeaderText: true,
            maxWidth: 120,
            filter: false,
            sortable: false,
            ...GridHelper.defaultFormInputCellRendererColDef,
            cellRendererParams: {
                title: 'Number of Paging Receivers',
                type: 'number',
                classList: 'text-center',
                min: 0,
                onFocusout: (control: FormControl) => {
                    this.handleFrequencyFocusOut(control);
                },
            } as FormInputCellRendererParams,
            headerTooltip:
                this.frequencyGridMessages
                    .numberOfPagingReceiversTooltipDescription,
        },
    ];

    frequencyGridOptions: GridOptions = {
        ...GridHelper.defaultFormFieldGridOptions,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
        columnDefs: this.frequencyColumnDefs,
        getRowId: GridHelper.defaultGetRowId,
        paginationPageSize: 150,
    };

    frequencyGridComponents: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [c: string]: any;
    } = {
        agColumnHeader: CheckboxHeaderRendererComponent,
    };

    emissionDesignatorOperationDialogDefaultOptions: Partial<MatDialogConfig> =
        {
            width: '800px',
            disableClose: true,
        };

    protected override readonly route = inject(ActivatedRoute);
    protected override readonly router = inject(Router);
    protected override readonly applicationPageFacadeService = inject(
        ApplicationPageFacadeService,
    );
    private readonly directEntryService = inject(DirectEntryService);
    private readonly formService = inject(SchedulesDAndHFormService);
    private readonly scheduleDFormService = inject(ScheduleDFormService);
    private readonly antennaFormService = inject(AntennaFormService);
    private readonly frequencyFormService = inject(FrequencyFormService);
    private readonly emissionDesignatorFormService = inject(
        EmissionDesignatorFormService,
    );
    private readonly dropdownService = inject(DropdownService);
    private readonly dialog = inject(MatDialog);
    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly schedulesDAndHSharedFormService = inject(
        SchedulesDAndHSharedFormService,
    );
    private readonly applicationService = inject(ApplicationService);
    private readonly populateScheduleDAsrDataService = inject(
        PopulateScheduleDAsrDataService,
    );
    private readonly frequencyFormattingService = inject(
        FrequencyFormattingService,
    );

    override form = this.formService.getFormDefinition();

    constructor() {
        super(
            inject(ActivatedRoute),
            inject(Router),
            inject(ApplicationPageFacadeService),
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
    }

    scheduleDs(): FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>> {
        return this.form.controls.scheduleDs as FormArray;
    }

    antennas(): FormArray<FormGroup<FormGroupType<AntennaFormModel>>> {
        return this.form.controls.antennas as FormArray;
    }

    frequencies(): FormArray<
        FormGroup<NestedFormGroupType<FrequencyFormModel>>
    > {
        return this.form.controls.frequencies as FormArray;
    }

    /**
     * Determines whether the location tab should be expanded.
     * The location tab is expanded if any Schedule D record is currently being edited.
     *
     * @returns true if the location tab should be expanded, false otherwise.
     */
    shouldExpandLocationTab(): boolean {
        return this.scheduleDs().controls.some(
            (scheduleD) => scheduleD.controls.isBeingEdited.value === true,
        );
    }

    /**
     * Finds if the list of schedule D records is invalid
     *
     * @returns true if the list is empty or any of the schedule D records are invalid
     */
    anyScheduleDsInvalid(): boolean {
        return this.scheduleDs().controls.some((scheduleD) => {
            const key = this.getScheduleDRowKeyForValidationMap(
                scheduleD.value as ScheduleDFormModel,
            );
            return this.scheduleDValidationState.get(key) === false;
        });
    }

    atMaximumFixedLocations(): boolean {
        return this.formService.atMaximumFixedLocations(this.scheduleDs());
    }

    checkSafeHarborButtonDisabled(): boolean {
        return (
            this.anyScheduleDsInvalid() ||
            this.anyAntennasInvalid() ||
            this.anyFrequenciesInvalid()
        );
    }

    onCheckSafeHarborClick(): void {
        if (this.hasChanges === false) {
            this.checkSafeHarborAndDisplayResults().subscribe();
        } else {
            // If there are changes to the form that the API does not know about,
            // we need to save the form data first before checking safe harbor.
            // This ensures that the safe harbor check is performed on the latest data.
            this.saveAndRebindForm()
                .pipe(
                    switchMap(() => this.checkSafeHarborAndDisplayResults()),
                    takeUntil(this.destroy$),
                )
                .subscribe();
        }
    }

    /**
     * Finds if the schedule D list has the maximum fixed locations exceeded error.
     * @returns true if there are too many fixed locations in the schedule D list
     */
    exceededMaximumFixedLocations(): boolean {
        const errors = this.scheduleDs().errors;
        if (!errors) {
            return false;
        }

        const key = nameOf<typeof ScheduleDValidationMessages>(
            'exceededMaximumFixedLocations',
        );

        return errors[key];
    }

    anyAntennasOrFrequenciesInvalid(): boolean {
        return this.anyAntennasInvalid() || this.anyFrequenciesInvalid();
    }

    anyAntennasInvalid(): boolean {
        return this.antennas().invalid || !this.allScheduleDsHaveAnAntenna();
    }

    anyFrequenciesInvalid(): boolean {
        return (
            this.frequencies().invalid ||
            !this.allUndeletedAntennasHaveAnUndeletedFrequency()
        );
    }

    areMultipleEmissionDesignatorsSelected(): boolean {
        return (
            this.frequencies().controls.filter(
                (control) =>
                    control.controls.emissionDesignatorsSelected.value === true,
            ).length > 1
        );
    }

    /**
     * Opens the emission designator operation dialog for all rows that have their emission designator selected
     */
    openMultiRowEmissionDesignatorOperation(): void {
        const operationControls: FormGroup<
            NestedFormGroupType<FrequencyFormModel>
        >[] = [];

        this.frequencies().controls.forEach((control) => {
            const shouldInclude =
                control.controls.emissionDesignatorsSelected.value;

            if (shouldInclude === true) {
                operationControls.push(control);
            }
        });

        this.dialog.open(EmissionDesignatorOperationComponent, {
            data: {
                controls: operationControls,
                saveEmissionDesignatorOperationFunction: (
                    form: FormGroup<
                        NestedFormGroupType<EmissionDesignatorOperation>
                    >,
                    frequencies: FormGroup<
                        NestedFormGroupType<FrequencyFormModel>
                    >[],
                ) =>
                    this.saveMultiRowEmissionDesignatorOperation(
                        form,
                        frequencies,
                    ),
            },
            ...this.emissionDesignatorOperationDialogDefaultOptions,
        });
    }

    /**
     * Opens the emission designator operation dialog for a single row
     * @param params
     */
    openSingleRowEmissionDesignatorOperation(
        params: EmissionDesignatorCellRendererParams,
    ): void {
        const control = this.frequencies().controls.at(params.node.rowIndex!);

        this.dialog.open(EmissionDesignatorOperationComponent, {
            data: {
                controls: [control],
                saveEmissionDesignatorOperationFunction: (
                    form: FormGroup<
                        NestedFormGroupType<EmissionDesignatorOperation>
                    >,
                    frequencies: FormGroup<
                        NestedFormGroupType<FrequencyFormModel>
                    >[],
                ) =>
                    this.saveSingleRowEmissionDesignatorOperation(
                        form,
                        frequencies,
                    ),
            },
            ...this.emissionDesignatorOperationDialogDefaultOptions,
        });
    }

    openMultiRowEmissionDesignatorOperationExistingLicense(): void {
        const operationControls: FormGroup<
            NestedFormGroupType<FrequencyFormModel>
        >[] = [];

        this.frequencies().controls.forEach((control) => {
            const shouldInclude =
                control.controls.emissionDesignatorsSelected.value;

            if (shouldInclude === true) {
                operationControls.push(control);
            }
        });

        this.dialog.open(ExistingLicenseEmissionDesignatorOperationComponent, {
            data: {
                controls: operationControls,
                saveEmissionDesignatorOperationFunction: (
                    form: FormGroup<
                        NestedFormGroupType<ExistingLicenseEmissionDesignatorOperation>
                    >,
                    frequencies: FormGroup<
                        NestedFormGroupType<FrequencyFormModel>
                    >[],
                ) =>
                    this.saveEmissionDesignatorOperationExistingLicense(
                        form,
                        frequencies,
                    ),
            },
            ...this.emissionDesignatorOperationDialogDefaultOptions,
        });
    }

    openSingleRowEmissionDesignatorOperationExistingLicense(
        params: EmissionDesignatorCellRendererParams,
    ): void {
        const control = this.frequencies().controls.at(params.node.rowIndex!);

        this.dialog.open(ExistingLicenseEmissionDesignatorOperationComponent, {
            data: {
                controls: [control],
                saveEmissionDesignatorOperationFunction: (
                    form: FormGroup<
                        NestedFormGroupType<ExistingLicenseEmissionDesignatorOperation>
                    >,
                    frequencies: FormGroup<
                        NestedFormGroupType<FrequencyFormModel>
                    >[],
                ) =>
                    this.saveEmissionDesignatorOperationExistingLicense(
                        form,
                        frequencies,
                    ),
            },
            ...this.emissionDesignatorOperationDialogDefaultOptions,
        });
    }

    /**
     * Marks the emission designators selected control to the given value for all frequencies
     * @param value
     */
    markEmissionDesignatorsSelected(value: boolean): void {
        this.frequencies().controls.forEach((control) => {
            (
                control.controls.emissionDesignatorsSelected as FormControl
            ).patchValue(value, { emitEvent: false });
        });
    }

    /**
     * Finds whether you can add a new antenna to the grid
     * @returns true if there are schedule D records in the list
     */
    shouldShowAddAntennaButton(): boolean {
        return this.scheduleDs().value.length > 0 && !this.isReadOnly();
    }

    /**
     * Finds whether you can add a new frequency to the grid
     * @returns true if there are antenna records in the list
     */
    shouldShowAddFrequencyButton(): boolean {
        return this.antennas().value.length > 0 && !this.isReadOnly();
    }

    /**
     * Adds a new antenna to the grid
     * @param scheduleD adds values to antenna if associated with location when added
     */
    addAntennaToGrid(
        scheduleD: FormGroup<FormGroupType<ScheduleDFormModel>> | null = null,
    ): void {
        const antennas = this.antennas();
        const form = this.antennaFormService.getFormDefinition();

        antennas.push(form);
        this.setupAntennaFormHandlers(form);

        if (scheduleD) {
            form.controls.locationNumber.patchValue(scheduleD.value.number!);
        }

        form.controls.tempId.patchValue(GridHelper.generateTempRowId());

        this.updateGrid(SchedulesDAndHGridType.antennas);
    }

    /**
     * Adds a new frequency to the grid and focuses the newly added row.
     * @param indexToInsert Optional zero-based form-array index at which to insert the frequency.
     * @param focusNewRow Whether to focus the row after adding it.
     */
    addFrequencyToGrid(
        indexToInsert: number | undefined = undefined,
        focusNewRow: boolean = true,
    ): void {
        const frequencies = this.frequencies();
        const form = this.frequencyFormService.getFormDefinition();

        if (indexToInsert !== undefined) {
            frequencies.insert(indexToInsert, form);
        } else {
            frequencies.push(form);
        }

        // Set a temporary ID for the frequency if it doesn't already have one
        // before any action that updates the grid
        if (form.controls.tempId.value === null) {
            (form.controls.tempId as FormControl).patchValue(
                GridHelper.generateTempRowId(),
            );
        }

        this.setupFrequencyFormHandlers(form);

        this.applicationPageFacadeService.applicationFormControlManager.disable(
            form.controls.antennaNumber,
            { emitEvent: false },
        );

        this.updateGrid(SchedulesDAndHGridType.frequencies);
        if (!focusNewRow) {
            return;
        }

        if (indexToInsert === undefined) {
            this.focusLastFrequencyRow();
        } else {
            this.focusFrequencyRow(indexToInsert);
        }
    }

    private focusLastFrequencyRow(): void {
        const frequenciesGridApi =
            this.gridApis[SchedulesDAndHGridType.frequencies];

        if (!frequenciesGridApi) {
            return;
        }

        frequenciesGridApi.paginationGoToLastPage();

        const lastDisplayedRowIndex =
            frequenciesGridApi.getDisplayedRowCount() - 1;

        if (lastDisplayedRowIndex < 0) {
            return;
        }

        // Wait for the grid to finish any pending re-renders, e.g. autoHeight rows,
        // before scrolling to and focusing the newly added last row.
        this.waitForStableRender(frequenciesGridApi)
            .pipe(
                // extra frame so the settle-triggering render has actually painted
                switchMap(() => this.animationFrame()),
                switchMap(() => {
                    frequenciesGridApi.ensureIndexVisible(
                        lastDisplayedRowIndex,
                        'bottom',
                    );

                    // one more frame so the scroll itself has painted before we focus
                    return this.animationFrame().pipe(
                        map(() => lastDisplayedRowIndex),
                    );
                }),
                tap((rowIndex) => {
                    frequenciesGridApi.setFocusedCell(
                        rowIndex,
                        nameOf<FrequencyFormModel>('locationNumber'),
                    );
                }),
                map(() => void 0),
            )
            .subscribe();
    }

    private focusFrequencyRow(rowIndex: number): void {
        const frequenciesGridApi =
            this.gridApis[SchedulesDAndHGridType.frequencies];

        if (!frequenciesGridApi) {
            return;
        }

        const pageSize = frequenciesGridApi.paginationGetPageSize();
        if (pageSize <= 0) {
            return;
        }

        frequenciesGridApi.paginationGoToPage(Math.floor(rowIndex / pageSize));
        frequenciesGridApi.setFocusedCell(
            rowIndex % pageSize,
            nameOf<FrequencyFormModel>('locationNumber'),
        );
    }

    /**
     * Resolves once the grid has gone quiet: no modelUpdated / rowHeightChanged /
     * paginationChanged events for some period in a row. debounceTime + take(1)
     * gives us exactly that "settled" semantics without a fixed guessed delay.
     * @param api The GridApi instance to observe for stability.
     * @returns An Observable that resolves once the grid has been stable for the specified duration.
     */
    private waitForStableRender(api: GridApi): Observable<void> {
        const events$ = merge(
            this.fromGridEvent(api, 'modelUpdated'),
            this.fromGridEvent(api, 'rowHeightChanged'),
            this.fromGridEvent(api, 'paginationChanged'),
            of(null), // fire the debounce timer immediately too, in case everything
            // already happened synchronously before we subscribed
        );

        return events$.pipe(
            debounceTime(80),
            take(1),
            map(() => void 0),
        );
    }

    /**
     * requestAnimationFrame as a one-shot Observable, cancelled on unsubscribe.
     */
    private animationFrame(): Observable<void> {
        return new Observable<void>((subscriber) => {
            const id = requestAnimationFrame(() => {
                subscriber.next();
                subscriber.complete();
            });
            return () => cancelAnimationFrame(id);
        });
    }

    /**
     * Wraps a GridApi event as an Observable via addEventListener/removeEventListener.
     * @param api The GridApi instance to observe for the event.
     * @param eventType The type of the event to listen for.
     * @returns An Observable that emits whenever the specified event occurs.
     */
    private fromGridEvent(
        api: GridApi,
        eventType: string,
    ): Observable<unknown> {
        return fromEventPattern(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (handler) => api.addEventListener(eventType as any, handler),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (handler) => api.removeEventListener(eventType as any, handler),
        );
    }

    onGridReady(params: GridReadyEvent, type: SchedulesDAndHGridType) {
        this.gridApis[type] = params.api;
        this.updateGrid(type);
    }

    updateGrid(type: SchedulesDAndHGridType) {
        let data = [];
        switch (type) {
            case SchedulesDAndHGridType.scheduleDs:
                data = cloneDeep((this.scheduleDs() as FormArray).value);
                break;
            case SchedulesDAndHGridType.antennas:
                data = cloneDeep((this.antennas() as FormArray).value);
                break;
            case SchedulesDAndHGridType.frequencies:
                data = cloneDeep((this.frequencies() as FormArray).value);
                break;
            default:
                throw new Error('Invalid grid type');
        }

        if (this.gridApis[type]) {
            this.gridApis[type].setGridOption('rowData', data);
        }

        return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initializeGridContext(gridType: SchedulesDAndHGridType): any {
        let formArray;
        switch (gridType) {
            case SchedulesDAndHGridType.scheduleDs:
                formArray = this.scheduleDs();
                break;
            case SchedulesDAndHGridType.antennas:
                formArray = this.antennas();
                break;
            case SchedulesDAndHGridType.frequencies:
                formArray = this.frequencies();
                break;
            default:
                throw new Error('Invalid grid type');
        }

        return {
            gridType: gridType,
            formArray: formArray,
        };
    }

    /*
     * Handler for the schedule D form, child component will call into this to mark that the main form is dirty.
     */
    scheduleDFormChanged(dirty: boolean): void {
        if (!this.hasChanges) {
            this.hasChanges = dirty;
        }
    }

    /*
     * Adds a new Schedule D form definition to the form array.
     */
    addScheduleDFormDefinition(): void {
        const scheduleDs = this.scheduleDs();
        scheduleDs.push(this.scheduleDFormService.getFormDefinition());
    }

    /**
     * Adds a new Antenna form definition to the form array.
     */
    addAntennaFormDefinition(): void {
        const antennas = this.antennas();
        antennas.push(this.antennaFormService.getFormDefinition());
    }

    /**
     * Adds a new Frequency form definition to the form array.
     */
    addFrequencyFormDefinition(): void {
        const frequencies = this.frequencies();
        frequencies.push(this.frequencyFormService.getFormDefinition());
    }

    /**
     * Adds a new Emission Designator form definition to the form array.
     * @param frequencyIndex
     */
    addEmissionDesignatorFormDefinition(frequencyIndex: number): void {
        const emissionDesignators = this.frequencies().at(frequencyIndex)
            .controls.emissionDesignators as FormArray;
        emissionDesignators.push(
            this.emissionDesignatorFormService.getFormDefinition(),
        );
    }

    /*
     * Handler for the schedule D form, adds a new schedule D form group to the form array and updates the grid.
     */
    saveScheduleD(
        scheduleDForm: FormGroup<FormGroupType<ScheduleDFormModel>>,
        isSubmitAction: boolean = false,
    ): void {
        const scheduleDs = this.scheduleDs();
        const isLocationFixed =
            this.scheduleDFormService.isFixed(scheduleDForm);

        // Find an existing schedule D form group with the same number
        const existingScheduleD = scheduleDs.controls.find(
            (scheduleD) =>
                scheduleD.value.number === scheduleDForm.value.number,
        );

        let currentScheduleD: FormGroup<FormGroupType<ScheduleDFormModel>>;

        if (existingScheduleD) {
            existingScheduleD.patchValue({
                ...scheduleDForm.value,
                // The location form may have been loaded before the location was persisted (e.g. save on tab change),
                // so keep the persisted ID instead of overwriting it with null.
                id:
                    existingScheduleD.controls.id.value ??
                    scheduleDForm.value.id,
            });

            if (
                this.formService.getAntennasForLocation(
                    scheduleDForm,
                    this.antennas(),
                ).length === 0
            ) {
                this.addAntennaToGrid(scheduleDForm);
            }

            currentScheduleD = existingScheduleD;

            /**
             * We need to find any locations that have a value for correspondingFixedLocation (CFL)
             * and check if that CFL is a fixed location still.
             * If it is not, we clear the CFL value.
             */
            this.scheduleDFormService.fixCorrespondingLocationsForScheduleDs(
                this.scheduleDs(),
            );
        } else {
            const newScheduleDFormGroup =
                this.scheduleDFormService.getFormDefinition();

            newScheduleDFormGroup.patchValue(scheduleDForm.value);
            scheduleDs.push(newScheduleDFormGroup);

            this.addAntennaToGrid(scheduleDForm);

            const savedScheduleD = scheduleDs.at(scheduleDs.length - 1);
            this.markAntennasAndFrequenciesAsLocationDeletedValueHandler(
                savedScheduleD,
            );

            currentScheduleD = savedScheduleD;
        }

        currentScheduleD.controls.isBeingEdited.patchValue(!isSubmitAction);

        this.formService
            .getAntennasForLocation(scheduleDForm, this.antennas())
            .forEach((antenna) => {
                this.antennaFormService.toggleFormControlsForLocationChange(
                    antenna,
                    isLocationFixed,
                );
            });

        const actionCodeChanged =
            this.scheduleDFormService.markActionCodeOnScheduleDChange(
                currentScheduleD.controls.actionCode,
                this.scheduleDs(),
                this.schedulesDAndHResponse!.scheduleDs || [],
            );

        if (actionCodeChanged) {
            this.updateGrid(SchedulesDAndHGridType.scheduleDs);
        }

        this.updateGrid(SchedulesDAndHGridType.antennas);
        this.updateGrid(SchedulesDAndHGridType.scheduleDs);

        // If this is a submit action, we need to initialize the validation state
        // and validate the current schedule D row.
        if (isSubmitAction) {
            this.initializeScheduleDValidationState();
            this.validateScheduleDRow(currentScheduleD);
        }

        this.form.markAsDirty();
        this.form.updateValueAndValidity({ emitEvent: true });

        if (isSubmitAction) {
            this.populateScheduleDAsrDataService.clearLastFetchedAsrNumber();
        }
    }

    /**
     * Finds if all the schedule D records have at least one antenna
     * @returns true if all locations have at least one antenna
     */
    allScheduleDsHaveAnAntenna(): boolean {
        const someLocationsMissingAntenna = this.scheduleDs().controls.some(
            (scheduleD) => {
                const locationNumber = scheduleD.value.number;
                return (
                    this.antennas().controls.find(
                        (antenna) =>
                            antenna.value.locationNumber === locationNumber,
                    ) === undefined
                );
            },
        );

        return !someLocationsMissingAntenna;
    }

    /**
     * Finds if all the undeleted antenna records have at least one frequency
     * @returns true if all undeleted antennas have at least one frequency
     */
    allUndeletedAntennasHaveAnUndeletedFrequency(): boolean {
        const someAntennasMissingFrequency = this.antennas()
            .controls.filter(
                (antenna) =>
                    antenna.value.isDeleted !== true &&
                    antenna.value.actionCode !== ActionCode.Delete,
            )
            .some((antenna) => {
                const antennaNumber = antenna.value.number;
                const locationNumber = antenna.value.locationNumber;

                return (
                    this.frequencies().controls.find(
                        (frequency) =>
                            frequency.value.isDeleted !== true &&
                            frequency.value.actionCode !== ActionCode.Delete &&
                            frequency.value.antennaNumber === antennaNumber &&
                            frequency.value.locationNumber === locationNumber,
                    ) === undefined
                );
            });

        return !someAntennasMissingFrequency;
    }

    /**
     * Saves the form data to the API before changing tabs
     * @param event The tab change event containing the index of the newly selected tab
     */
    onTabChange(event: MatTabChangeEvent): void {
        if (this.hasChanges === true) {
            this.saveAndRebindForm().pipe(takeUntil(this.destroy$)).subscribe();
        }

        if (event.index === 0) {
            if (!this.gridApis[SchedulesDAndHGridType.antennas]) {
                return;
            }

            this.gridApis[SchedulesDAndHGridType.antennas].redrawRows();
            this.gridApis[SchedulesDAndHGridType.antennas].refreshHeader();
        } else {
            if (!this.gridApis[SchedulesDAndHGridType.frequencies]) {
                return;
            }

            this.gridApis[SchedulesDAndHGridType.frequencies].redrawRows();
            this.gridApis[SchedulesDAndHGridType.frequencies].refreshHeader();
        }
    }

    /**
     * Performs the operation to save the emission designators for a single row
     * @param form
     * @param frequencies
     */
    saveSingleRowEmissionDesignatorOperation(
        form: FormGroup<NestedFormGroupType<EmissionDesignatorOperation>>,
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
    ): void {
        frequencies.forEach((frequency) => {
            const emissionDesignatorsFormArray = frequency.controls
                .emissionDesignators as FormArray;

            emissionDesignatorsFormArray.clear();

            for (
                let i = 0;
                i <
                (form.controls.addedEmissionDesignators as FormArray).length;
                i++
            ) {
                const emissionDesignatorForm =
                    this.emissionDesignatorFormService.getFormDefinition();
                emissionDesignatorsFormArray.push(emissionDesignatorForm);
            }

            const sortedEmissionDesignators =
                this.emissionDesignatorFormService.getSortedEmissionDesignatorValues(
                    form.controls.addedEmissionDesignators
                        .value as EmissionDesignator[],
                );

            emissionDesignatorsFormArray.patchValue(sortedEmissionDesignators);
        });

        this.postEmissionDesignatorOperation();
    }

    /**
     * Performs the operation to save the emission designators for multiple rows
     * @param form
     * @param frequencies
     */
    saveMultiRowEmissionDesignatorOperation(
        form: FormGroup<NestedFormGroupType<EmissionDesignatorOperation>>,
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
    ): void {
        frequencies.forEach((frequency) => {
            const emissionDesignatorsFormArray = frequency.controls
                .emissionDesignators as FormArray;

            // Delete the emission designators that are marked for deletion
            for (
                let i =
                    (form.controls.deletedEmissionDesignators as FormArray)
                        .length - 1;
                i >= 0;
                i--
            ) {
                const control = (
                    form.controls.deletedEmissionDesignators as FormArray
                ).at(i);
                const code = control.value.code;
                const index = emissionDesignatorsFormArray.controls.findIndex(
                    (emissionDesignator) =>
                        emissionDesignator.value.code === code,
                );

                if (index >= 0) {
                    emissionDesignatorsFormArray.removeAt(index);
                }
            }

            // Add the emission designators
            (
                form.controls.addedEmissionDesignators as FormArray
            ).controls.forEach((control) => {
                const emissionDesignatorForm =
                    this.emissionDesignatorFormService.getFormDefinition();
                emissionDesignatorForm.patchValue(control.value);

                // Only add the emission designator to the list if it doesn't already have it
                if (
                    !emissionDesignatorsFormArray.controls.some(
                        (emissionDesignator) =>
                            emissionDesignator.value.code ===
                            control.value.code,
                    )
                ) {
                    emissionDesignatorsFormArray.push(emissionDesignatorForm);
                }
            });

            const sortedEmissionDesignators =
                this.emissionDesignatorFormService.getSortedEmissionDesignatorValues(
                    emissionDesignatorsFormArray.value as EmissionDesignator[],
                );

            emissionDesignatorsFormArray.patchValue(sortedEmissionDesignators);
        });

        this.postEmissionDesignatorOperation();
    }

    /**
     * Performs the operation to save the emission designators for an existing license frequencies.
     * Single row and multi-row operations are handled here.
     * @param form
     * @param frequencies
     */
    saveEmissionDesignatorOperationExistingLicense(
        form: FormGroup<
            NestedFormGroupType<ExistingLicenseEmissionDesignatorOperation>
        >,
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
    ): void {
        const singleRowOperation = frequencies.length > 1 ? false : true;

        const commonEmissionDesignatorsFromAllFrequencies =
            this.emissionDesignatorFormService.getCommonEmissionDesignatorsFromAllFrequencies(
                frequencies,
            );

        frequencies.forEach((frequency) => {
            const frequencyEmissionDesignators = frequency.controls
                .emissionDesignators as FormArray<
                FormGroup<FormGroupType<EmissionDesignator>>
            >;

            const operationEmissionDesignators = (
                form.controls.emissionDesignators as FormArray<
                    FormGroup<FormGroupType<EmissionDesignator>>
                >
            ).value as EmissionDesignator[];

            // Take care of the emission designators that are NO action.
            operationEmissionDesignators
                .filter((x) => x.actionCode === null)
                .forEach((operationEmissionDesignator) => {
                    const matchingFrequencyEmissionDesignatorIndex =
                        frequencyEmissionDesignators.controls.findIndex(
                            (x) =>
                                x.value.code ===
                                operationEmissionDesignator.code,
                        );

                    // Don't worry about it not being on the frequency, for multi-row operations sometimes
                    // the emission designator is not on all the frequencies coming from the form.
                    if (matchingFrequencyEmissionDesignatorIndex === -1) {
                        return;
                    }

                    const matchingFrequencyEmissionDesignator =
                        frequencyEmissionDesignators.at(
                            matchingFrequencyEmissionDesignatorIndex,
                        );

                    // If the operation ED is marked as no action and the matching frequency ED is marked as delete,
                    // then the frequency ED should be marked as no action.
                    if (
                        matchingFrequencyEmissionDesignator.controls.actionCode
                            .value === ActionCode.Delete
                    ) {
                        matchingFrequencyEmissionDesignator.controls.actionCode.patchValue(
                            null,
                        );
                    }
                });

            // Take care of the emission designators that are Delete action.
            operationEmissionDesignators
                .filter((x) => x.actionCode === ActionCode.Delete)
                .forEach((operationEmissionDesignator) => {
                    const matchingFrequencyEmissionDesignatorIndex =
                        frequencyEmissionDesignators.controls.findIndex(
                            (x) =>
                                x.value.code ===
                                operationEmissionDesignator.code,
                        );

                    // Don't worry about it not being on the frequency, for multi-row operations sometimes
                    // the emission designator is not on all the frequencies coming from the form.
                    if (matchingFrequencyEmissionDesignatorIndex === -1) {
                        return;
                    }

                    const matchingFrequencyEmissionDesignator =
                        frequencyEmissionDesignators.at(
                            matchingFrequencyEmissionDesignatorIndex,
                        );

                    // If the operation ED is marked as delete and the matching frequency ED is marked as add,
                    // then the frequency ED should be removed from the list since it is no longer needed on the frequency.
                    if (
                        matchingFrequencyEmissionDesignator.controls.actionCode
                            .value === ActionCode.Add
                    ) {
                        frequencyEmissionDesignators.removeAt(
                            matchingFrequencyEmissionDesignatorIndex,
                        );
                    } else {
                        matchingFrequencyEmissionDesignator.controls.actionCode.patchValue(
                            ActionCode.Delete,
                        );
                    }
                });

            // Take care of the emission designators that are Add action.
            operationEmissionDesignators
                .filter((x) => x.actionCode === ActionCode.Add)
                .forEach((operationEmissionDesignator) => {
                    const matchingFrequencyEmissionDesignatorIndex =
                        frequencyEmissionDesignators.controls.findIndex(
                            (x) =>
                                x.value.code ===
                                operationEmissionDesignator.code,
                        );

                    // If the operation ED is marked as add and there isn't a matching frequency ED,
                    // then we need to add it to the frequency's ED list.
                    if (matchingFrequencyEmissionDesignatorIndex === -1) {
                        const emissionDesignatorForm =
                            this.emissionDesignatorFormService.getFormDefinition();

                        emissionDesignatorForm.patchValue(
                            operationEmissionDesignator,
                        );

                        frequencyEmissionDesignators.push(
                            emissionDesignatorForm,
                        );
                    }
                    // If it is on the frequency and this is a multi-row operation, then we need to check if the
                    // action code on the frequency's ED is delete because if it is, then that ED needs to be set
                    // as no action.
                    else if (singleRowOperation === false) {
                        const matchingFrequencyEmissionDesignator =
                            frequencyEmissionDesignators.at(
                                matchingFrequencyEmissionDesignatorIndex,
                            );

                        if (
                            matchingFrequencyEmissionDesignator.controls
                                .actionCode.value === ActionCode.Delete
                        ) {
                            matchingFrequencyEmissionDesignator.controls.actionCode.patchValue(
                                null,
                            );
                        }
                    }
                });

            // Take care of the emission designators that are on the frequency but not included in the operation.
            const indexesToRemoveEmissionDesignators: number[] = [];

            frequencyEmissionDesignators.controls.forEach(
                (frequencyEmissionDesignator, index) => {
                    const matchingOperationEmissionDesignatorIndex =
                        operationEmissionDesignators.findIndex(
                            (x) =>
                                x.code ===
                                frequencyEmissionDesignator.value.code,
                        );

                    const matchingCommonEmissionDesignatorIndex =
                        commonEmissionDesignatorsFromAllFrequencies.findIndex(
                            (x) =>
                                x.code ===
                                frequencyEmissionDesignator.value.code,
                        );

                    // If the emission designator is NOT included in the operation, then we might need to remove it.
                    if (
                        matchingOperationEmissionDesignatorIndex === -1 &&
                        matchingCommonEmissionDesignatorIndex !== -1 &&
                        (singleRowOperation === true ||
                            frequencyEmissionDesignator.controls.actionCode
                                .value === ActionCode.Add)
                    ) {
                        indexesToRemoveEmissionDesignators.push(index);
                    }
                },
            );

            // Traverse the indexes in reverse order to prevent index shifting
            indexesToRemoveEmissionDesignators.reverse().forEach((index) => {
                frequencyEmissionDesignators.removeAt(index);
            });

            const sortedEmissionDesignators =
                this.emissionDesignatorFormService.getSortedEmissionDesignatorValues(
                    frequencyEmissionDesignators.value as EmissionDesignator[],
                );

            frequencyEmissionDesignators.patchValue(sortedEmissionDesignators);

            this.handleFrequencyFocusOut(
                frequency.controls.emissionDesignators as FormControl,
            );
        });

        this.postEmissionDesignatorOperation();
    }

    /**
     * Finds the title for the location tab based on whether a location is being edited
     *
     * @returns the title for the location tab
     */
    getLocationTabTitle(): string {
        const isAScheduleDBeingEdited = this.scheduleDs().controls.some(
            (scheduleD) => scheduleD.value.isBeingEdited,
        );

        if (isAScheduleDBeingEdited) {
            return 'Editing Location';
        }

        return 'Add a New Location';
    }

    /**
     * Opt out of the base auto-focus; Schedule D's location form sets focus per step.
     */
    protected override shouldAutoFocusFirstField(): boolean {
        return false;
    }

    protected override bindForm(response: SchedulesDAndHResponse): void {
        this.schedulesDAndHResponse = cloneDeep(response);

        // Capture which location is being edited so the state survives a rebind (e.g. save on tab change),
        // since the API response does not include it.
        const locationNumbersBeingEdited = this.scheduleDs()
            .controls.filter((scheduleD) => scheduleD.value.isBeingEdited)
            .map((scheduleD) => scheduleD.value.number);

        this.scheduleDs().clear({
            emitEvent: false,
        });
        response.scheduleDs.forEach(() => {
            this.addScheduleDFormDefinition();
        });

        this.antennas().clear({
            emitEvent: false,
        });
        response.antennas.forEach(() => {
            this.addAntennaFormDefinition();
        });

        this.frequencies().clear({
            emitEvent: false,
        });
        response.frequencies.forEach((frequency, index) => {
            this.addFrequencyFormDefinition();

            frequency.emissionDesignators.forEach(() => {
                this.addEmissionDesignatorFormDefinition(index);
            });

            // Sort the emission designators on page load
            frequency.emissionDesignators =
                this.emissionDesignatorFormService.getSortedEmissionDesignatorValues(
                    frequency.emissionDesignators,
                );
        });

        // This is necessary because the form handlers rely on the form structure to set up their value change handlers.
        this.form.patchValue(response);

        this.setupFormHandlers();

        this.addScheduleDReady = true;

        // Once all form handlers are installed, we can officially patch the form with the correct value
        this.patchFormValue(response);

        this.scheduleDs()
            .controls.filter((scheduleD) =>
                locationNumbersBeingEdited.includes(scheduleD.value.number),
            )
            .forEach((scheduleD) =>
                scheduleD.controls.isBeingEdited.patchValue(true, {
                    emitEvent: false,
                }),
            );

        this.updateGrid(SchedulesDAndHGridType.scheduleDs);
        this.updateGrid(SchedulesDAndHGridType.antennas);
        this.updateGrid(SchedulesDAndHGridType.frequencies);

        // The form controls were recreated, so any rendered grid cells must be redrawn to bind to the new controls.
        this.redrawAllGrids();

        // Initialize the validation state for the binded schedule D rows and validate them accordingly.
        this.initializeScheduleDValidationState();
        this.validateAllScheduleDRows();

        // The form now matches the API, same as a fresh page load
        this.form.markAsPristine();
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<SchedulesDAndHResponse> {
        return this.directEntryService.getSchedulesDAndH(applicationId);
    }

    protected override saveFormDataToApi(
        request: SchedulesDAndHRequest,
    ): Observable<StepValidationResult> {
        return this.directEntryService.saveSchedulesDAndH(request);
    }

    protected override generateRequestObject(): SchedulesDAndHRequest {
        return {
            ...this.getValue(),
            applicationId: this.applicationId!,
        };
    }

    /**
     * Performs shared logic after an emission designator operation has been completed
     */
    private postEmissionDesignatorOperation(): void {
        this.updateGrid(SchedulesDAndHGridType.frequencies);

        // Refresh the header to deselect the checkbox
        this.gridApis[SchedulesDAndHGridType.frequencies].refreshHeader();

        // Uncheck all emission designator checkboxes after the operation
        this.frequencies().controls.forEach((frequency) => {
            (
                frequency.controls.emissionDesignatorsSelected as FormControl
            ).patchValue(false);
        });

        this.form.markAsDirty();
        this.form.updateValueAndValidity({ emitEvent: true });
    }

    /**
     * Loads the schedule D form that corresponds to the row clicked into the schedule D base component.
     * @param params the parameters for the cell renderer
     */
    private loadScheduleDIntoForm(params: SingleActionCellRendererParams) {
        const scheduleD = this.scheduleDs().controls.find(
            (x) => x.controls.number.value === params.data.number!,
        );

        if (scheduleD) {
            this.scheduleDs()
                .controls.filter((control) => control !== scheduleD)
                .forEach((control) => {
                    if (control.controls.isBeingEdited.value === true) {
                        const rowId =
                            control.controls.id.value ||
                            control.controls.tempId.value;
                        if (rowId === null) {
                            throw new Error('Row ID is not defined');
                        }

                        control.controls.isBeingEdited.patchValue(false);
                        this.updateRowData(
                            SchedulesDAndHGridType.scheduleDs,
                            rowId.toString(),
                            control.value,
                        );
                    }
                });

            scheduleD.controls.isBeingEdited.patchValue(true);

            const rowId = params.data.id || params.data.tempId;
            if (rowId) {
                this.updateRowData(
                    SchedulesDAndHGridType.scheduleDs,
                    rowId,
                    scheduleD.value,
                );
            } else {
                throw new Error('Current row ID is not defined');
            }

            this.scheduleDBaseComponent.loadScheduleD(scheduleD);
        }
    }

    /**
     * Updates a specific row in the grid with the provided row data.
     * @param type The type of grid to update
     * @param rowId The row Id to update
     * @param rowData The data to update the row with
     */
    private updateRowData(
        type: SchedulesDAndHGridType,
        rowId: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rowData: any,
    ) {
        const rowNode = this.gridApis[type].getRowNode(rowId);
        if (rowNode) {
            rowNode.updateData(rowData);
        }
    }

    /**
     * Copies a frequency from the grid and appends a new frequency with the same values to the end of the list
     * @param params the parameters for the button cell renderer, including the row index
     */
    private copyFrequencyToGrid(params: ButtonCellRendererParams) {
        const rowIndex = params.node.rowIndex;
        if (rowIndex === null) {
            return;
        }

        const frequency = this.frequencies().at(rowIndex);
        const insertAtIndex = rowIndex + 1;

        // Copy the frequency value except for the ID so the API recognizes it as a new record
        const copiedFrequencyValue = cloneDeep(frequency.value);
        copiedFrequencyValue.id = null;
        copiedFrequencyValue.tempId = GridHelper.generateTempRowId();

        this.addFrequencyToGrid(insertAtIndex, false);
        const newFrequency = this.frequencies().at(insertAtIndex);
        copiedFrequencyValue.emissionDesignators.forEach(() => {
            this.addEmissionDesignatorFormDefinition(insertAtIndex);
        });
        newFrequency.patchValue(copiedFrequencyValue);

        if (
            this.applicationPageFacadeService.applicationStateService.IsExistingLicenseApplication()
        ) {
            (newFrequency.controls.actionCode as FormControl).patchValue(
                ActionCode.Add,
            );

            // Emission designators should have action code set to add when it is copied and is an existing license.
            (
                newFrequency.controls.emissionDesignators as FormArray<
                    FormGroup<FormGroupType<EmissionDesignator>>
                >
            ).controls.forEach((emissionDesignator) => {
                (
                    emissionDesignator.controls.actionCode as FormControl
                ).patchValue(ActionCode.Add);
            });
        }

        this.hasChanges = true;

        // Need to manually mark as dirty since the form is not being updated directly
        this.frequencies().markAsDirty();

        this.focusFrequencyRow(insertAtIndex);
    }

    private setupFormHandlers(): void {
        this.scheduleDs().controls.forEach((location) => {
            this.setupScheduleDFormHandlers(location);
        });

        this.antennas().controls.forEach((antenna) => {
            this.setupAntennaFormHandlers(antenna);
        });

        this.frequencies().controls.forEach((frequency) => {
            this.setupFrequencyFormHandlers(frequency);
        });

        this.markAntennaAsDeletedHandler(this.antennas().controls);
        this.markFrequencyAsDeletedHandler(this.frequencies().controls);
    }

    /**
     * Saves the form to the API, then re-fetches and re-binds it since entities could have been added or deleted
     * and ids could have changed after saving.
     * @returns the re-fetched form data
     */
    private saveAndRebindForm(): Observable<SchedulesDAndHResponse> {
        const request = this.generateRequestObject();

        return this.saveFormDataToApi(request).pipe(
            tap((stepValResult: StepValidationResult) => {
                this.applicationPageFacadeService.stepNavigationService.setCurrentStepValidation(
                    stepValResult.passedFinalValidation,
                );
            }),
            switchMap(() => this.getFormDataFromApi(this.applicationId!)),
            tap((response: SchedulesDAndHResponse) => {
                this.bindForm(response);

                // Set after binding since patching the form emits value changes that update hasChanges
                this.hasChanges = false;
            }),
        );
    }

    /**
     * Redraws the rows of every rendered grid. Form cell renderers resolve their form control once when
     * created, so they must be recreated after the form arrays are rebuilt to bind to the new controls.
     */
    private redrawAllGrids(): void {
        Object.values(this.gridApis).forEach((gridApi) => {
            // Grids within inactive tabs are destroyed since tab content is lazy loaded
            if (gridApi && !gridApi.isDestroyed()) {
                gridApi.redrawRows();
                gridApi.refreshHeader();
            }
        });
    }

    private setupScheduleDFormHandlers(
        scheduleD: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): void {
        this.markAntennasAndFrequenciesAsLocationDeletedValueHandler(scheduleD);
    }

    private setupAntennaFormHandlers(
        antenna: FormGroup<FormGroupType<AntennaFormModel>>,
    ): void {
        this.antennaChangeHandler(antenna);
        this.antennaLocationNumberChangeHandler(antenna);
        this.antennaOmniDirectionalChangeHandler(antenna);
        this.markFrequenciesAsAntennaDeletedValueHandler(antenna);
    }

    private setupFrequencyFormHandlers(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
    ): void {
        this.frequencyChangeHandler(frequency);
        this.frequencyLocationNumberChangeHandler(frequency);
        this.frequencyAntennaNumberChangeHandler(frequency);
        this.frequencyTypeChangeHandler(frequency);
    }

    /**
     * Retrieves the unique row key for a given Schedule D form value for the validation map.
     *
     * @param formValue The Schedule D form value.
     * @returns The unique row key as a string.
     */
    private getScheduleDRowKeyForValidationMap(
        formValue: ScheduleDFormModel,
    ): string {
        const scheduleDValidationMapRowKey = formValue.id ?? formValue.tempId;

        if (scheduleDValidationMapRowKey == null) {
            throw new Error(
                'Schedule D row must have either ID or temporary ID defined.',
            );
        }

        return String(scheduleDValidationMapRowKey);
    }

    /**
     * Initializes the validity state for each Schedule D row.
     *
     * AG Grid row rules need a synchronous boolean, so we pre-populate the map with
     * a default value before the async validation completes.
     */
    private initializeScheduleDValidationState(): void {
        this.scheduleDs().controls.forEach((scheduleDForm) => {
            const key = this.getScheduleDRowKeyForValidationMap(
                scheduleDForm.value as ScheduleDFormModel,
            );

            if (!this.scheduleDValidationState.has(key)) {
                this.scheduleDValidationState.set(key, true);
            }
        });
    }

    /**
     * Runs the async Schedule D validation and stores the result in the map.
     * Once the async result resolves, redraw the grid so AG Grid re-evaluates its
     * rowClassRules against the new boolean value.
     */
    private validateScheduleDRow(
        form: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): void {
        const key = this.getScheduleDRowKeyForValidationMap(
            form.value as ScheduleDFormModel,
        );

        // Default to true while the async validation is still in flight. This keeps
        // the row styling logic synchronous until the actual result is known.
        this.scheduleDValidationState.set(key, true);

        this.scheduleDFormService
            .isScheduleDValid(form.value as ScheduleDFormModel)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (isValid) => {
                    this.scheduleDValidationState.set(key, isValid);
                    this.gridApis[
                        SchedulesDAndHGridType.scheduleDs
                    ]?.redrawRows();
                },
                error: () => {
                    this.scheduleDValidationState.set(key, false);
                    this.gridApis[
                        SchedulesDAndHGridType.scheduleDs
                    ]?.redrawRows();
                },
            });
    }

    /**
     * Validates every Schedule D row and populates the validity map.
     */
    private validateAllScheduleDRows(): void {
        this.scheduleDs().controls.forEach((scheduleDControl) => {
            this.validateScheduleDRow(scheduleDControl);
        });
    }

    /**
     * Marks all associated antennas and frequencies as deleted when the location is marked as deleted
     * @param scheduleD
     */
    private markAntennasAndFrequenciesAsLocationDeletedValueHandler(
        scheduleD: FormGroup<FormGroupType<ScheduleDFormModel>>,
    ): void {
        scheduleD.controls.isDeleted.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((isDeleted: boolean | null) => {
                if (isDeleted === null) {
                    return;
                }

                scheduleD.controls.isDeleted.markAsDirty();

                const actionCodeChanged =
                    this.schedulesDAndHSharedFormService.refreshFormActionCode(
                        scheduleD,
                        this.schedulesDAndHResponse!.scheduleDs,
                    );

                this.formService
                    .getAntennasForLocation(scheduleD, this.antennas())
                    .forEach((antenna) => {
                        antenna.controls.isDeleted.patchValue(isDeleted, {
                            emitEvent: false,
                        });

                        antenna.controls.isDeleted.markAsDirty();

                        this.schedulesDAndHSharedFormService.refreshFormActionCode(
                            antenna,
                            this.schedulesDAndHResponse!.antennas,
                        );
                    });

                this.formService
                    .getFrequenciesForLocation(scheduleD, this.frequencies())
                    .forEach((frequency) => {
                        (
                            frequency.controls.isDeleted as FormControl
                        ).patchValue(isDeleted, {
                            emitEvent: false,
                        });

                        frequency.controls.isDeleted.markAsDirty();

                        this.schedulesDAndHSharedFormService.refreshFormActionCode(
                            frequency,
                            this.schedulesDAndHResponse!.frequencies,
                        );
                    });

                if (actionCodeChanged) {
                    this.updateGrid(SchedulesDAndHGridType.scheduleDs);
                }

                this.updateGrid(SchedulesDAndHGridType.antennas);
                this.updateGrid(SchedulesDAndHGridType.frequencies);
            });
    }

    /**
     * Marks all associated frequencies as deleted when the antenna is marked as deleted
     * @param antenna
     */
    private markFrequenciesAsAntennaDeletedValueHandler(
        antenna: FormGroup<FormGroupType<AntennaFormModel>>,
    ): void {
        antenna.controls.isDeleted.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((isDeleted) => {
                if (isDeleted === null) {
                    return;
                }

                this.formService
                    .getFrequenciesForAntenna(antenna, this.frequencies())
                    .forEach((frequency) => {
                        const control = frequency.controls
                            .isDeleted as FormControl;
                        control.patchValue(isDeleted, {
                            emitEvent: false,
                        });

                        control.markAsDirty();

                        this.schedulesDAndHSharedFormService.refreshFormActionCode(
                            frequency,
                            this.schedulesDAndHResponse!.frequencies,
                        );
                    });

                this.updateGrid(SchedulesDAndHGridType.frequencies);
            });
    }

    /**
     * Toggles validators on certain antenna form controls
     */
    private antennaChangeHandler(
        antenna: FormGroup<FormGroupType<AntennaFormModel>>,
    ): void {
        antenna.valueChanges
            .pipe(
                // Ensuring that the validators are toggled once on setup
                // before any value changes emissions are made
                startWith(antenna.value),
                takeUntil(this.destroy$),
                tap(() => {
                    this.antennaFormService.toggleValidators(antenna);
                }),
            )
            .subscribe();
    }

    private antennaOmniDirectionalChangeHandler(
        antenna: FormGroup<FormGroupType<AntennaFormModel>>,
    ): void {
        const omniDirectionalControl = antenna.controls.isOmniDirectional;

        omniDirectionalControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                startWith(omniDirectionalControl.value),
            )
            .subscribe(() => {
                this.antennaFormService.toggleFormControlsForOmniDirectionalChange(
                    antenna,
                );
            });
    }

    private antennaLocationNumberChangeHandler(
        antenna: FormGroup<FormGroupType<AntennaFormModel>>,
    ): void {
        const locationNumberControl = antenna.controls.locationNumber;

        locationNumberControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.antennaFormService.refreshAntennaNumbers(
                    this.form.controls.antennas as FormArray,
                );

                const location = this.formService.getAntennaLocation(
                    antenna,
                    this.scheduleDs(),
                );

                this.antennaFormService.toggleFormControlsForLocationChange(
                    antenna,
                    this.scheduleDFormService.isFixed(location),
                );

                this.antennaFormService.toggleFormControlsForOmniDirectionalChange(
                    antenna,
                );

                if (location.controls.isDeleted.value !== null) {
                    antenna.controls.isDeleted.patchValue(
                        location.controls.isDeleted.value,
                        {
                            emitEvent: false,
                        },
                    );
                }

                if (
                    this.applicationStateService.IsExistingLicenseApplication()
                ) {
                    if (antenna.controls.isDeleted.value === true) {
                        antenna.controls.actionCode.patchValue(
                            ActionCode.Delete,
                            { emitEvent: false },
                        );
                    } else {
                        this.antennaFormService.markActionCodeOnAntennaChange(
                            locationNumberControl,
                            this.antennas(),
                            this.schedulesDAndHResponse!.antennas || [],
                        );
                    }
                }

                // Update the antennas grid if the antenna is ready to be displayed.
                if (
                    antenna.controls.id.value !== null ||
                    antenna.controls.tempId.value !== null
                ) {
                    this.updateGrid(SchedulesDAndHGridType.antennas);
                }
            });
    }

    /**
     * Toggles validators on certain frequency form controls
     */
    private frequencyChangeHandler(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
    ): void {
        frequency.valueChanges
            .pipe(
                // Ensuring that the validators are toggled once on setup
                // before any value changes emissions are made
                startWith(frequency.value),
                takeUntil(this.destroy$),
                tap(() => {
                    this.frequencyFormService.toggleValidators(frequency);
                }),
            )
            .subscribe();
    }

    private frequencyLocationNumberChangeHandler(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
    ): void {
        const locationNumberControl = frequency.controls
            .locationNumber as FormControl;

        locationNumberControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((locationNumber) => {
                const location = this.formService.getFrequencyLocation(
                    frequency,
                    this.scheduleDs(),
                );

                if (location === null || location === undefined) {
                    // Location can be null or undefined if copying a newly added row before the location number is set.
                    return;
                }

                this.frequencyFormService.toggleFormControlsForLocationChange(
                    frequency,
                    locationNumber,
                );

                if (location.controls.isDeleted.value !== null) {
                    (frequency.controls.isDeleted as FormControl).patchValue(
                        location.controls.isDeleted.value,
                        {
                            emitEvent: false,
                        },
                    );
                }

                if (
                    this.applicationStateService.IsExistingLicenseApplication()
                ) {
                    if (frequency.controls.isDeleted.value === true) {
                        (
                            frequency.controls.actionCode as FormControl
                        ).patchValue(ActionCode.Delete, { emitEvent: false });
                    } else {
                        this.frequencyFormService.markActionCodeOnFrequencyChange(
                            locationNumberControl,
                            this.frequencies(),
                            this.schedulesDAndHResponse!.frequencies || [],
                        );
                    }
                }

                this.updateGrid(SchedulesDAndHGridType.frequencies);

                if (this.gridApis[SchedulesDAndHGridType.frequencies]) {
                    // Redraw the row to ensure antenna # dropdown options are updated. This must happen after the grid
                    // is updated, since the dropdown options are built from the row data's location number.
                    const rowId =
                        frequency.controls.id.value ??
                        frequency.controls.tempId.value;
                    const rowNode =
                        this.gridApis[
                            SchedulesDAndHGridType.frequencies
                        ].getRowNode(rowId);
                    if (rowNode) {
                        this.gridApis[
                            SchedulesDAndHGridType.frequencies
                        ].redrawRows({
                            rowNodes: [rowNode],
                        });
                    }
                }
            });
    }

    private frequencyAntennaNumberChangeHandler(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
    ): void {
        const antennaNumberControl = frequency.controls
            .antennaNumber as FormControl;

        antennaNumberControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((antennaNumber) => {
                const antenna = this.formService.getFrequencyAntenna(
                    frequency,
                    this.antennas(),
                );

                this.frequencyFormService.toggleFormControlsForAntennaChange(
                    frequency,
                    antennaNumber,
                );

                if (antenna && antenna.controls.isDeleted.value !== null) {
                    frequency.controls.isDeleted.patchValue(
                        (antenna.controls.isDeleted as FormControl).value,
                        {
                            emitEvent: false,
                        },
                    );
                }

                if (
                    this.applicationStateService.IsExistingLicenseApplication()
                ) {
                    if (frequency.controls.isDeleted.value === true) {
                        (
                            frequency.controls.actionCode as FormControl
                        ).patchValue(ActionCode.Delete, { emitEvent: false });
                    } else {
                        this.frequencyFormService.markActionCodeOnFrequencyChange(
                            antennaNumberControl,
                            this.frequencies(),
                            this.schedulesDAndHResponse!.frequencies || [],
                        );
                    }
                }

                this.updateGrid(SchedulesDAndHGridType.frequencies);
            });
    }

    private frequencyTypeChangeHandler(
        frequency: FormGroup<NestedFormGroupType<FrequencyFormModel>>,
    ): void {
        const frequencyTypeControl = frequency.controls
            .frequencyTypeCode as FormControl;

        frequencyTypeControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                // Do not override frequency low and frequency high unless user selected new frequency type code.
                if (!frequencyTypeControl.dirty) {
                    return;
                }

                const frequencyPair =
                    FrequencyTypeToFrequencyPair[value as FrequencyTypeCode];

                if (frequencyPair) {
                    (frequency.controls.frequencyLow as FormControl).patchValue(
                        this.frequencyFormattingService.getFormattedFrequency(
                            frequencyPair.low,
                        ),
                        { emitEvent: false },
                    );

                    (
                        frequency.controls.frequencyHigh as FormControl
                    ).patchValue(
                        this.frequencyFormattingService.getFormattedFrequency(
                            frequencyPair.high,
                        ),
                        {
                            emitEvent: false,
                        },
                    );

                    const actionCodeChanged =
                        this.frequencyFormService.markActionCodeOnFrequencyChange(
                            frequencyTypeControl,
                            this.frequencies(),
                            this.schedulesDAndHResponse!.frequencies || [],
                        );

                    if (actionCodeChanged) {
                        this.updateGrid(SchedulesDAndHGridType.frequencies);
                    }
                }
            });
    }

    private handleAntennaFocusOut(control: FormControl): void {
        // We don't need to update the action code here for new applications
        // because the action code will always be ActionCode.Add
        if (!this.applicationStateService.IsExistingLicenseApplication()) {
            return;
        }

        // The API response will return null for empty fields, where the form will have an empty string
        // if you leave the field empty after focusing out. We want to set the value to null if this is the case
        // so the comparison with the API response works correctly and it won't set the action code to modified
        // when it shouldn't.
        if (control.value === '') {
            control.patchValue(null, { emitEvent: false });
        }

        const actionCodeChanged =
            this.antennaFormService.markActionCodeOnAntennaChange(
                control,
                this.antennas(),
                this.schedulesDAndHResponse!.antennas || [],
            );

        if (actionCodeChanged) {
            setTimeout(() => {
                this.updateGrid(SchedulesDAndHGridType.antennas);
            });
        }
    }

    private handleFrequencyFocusOut(control: FormControl): void {
        // We don't need to update the action code here for new applications
        // because the action code will always be ActionCode.Add
        if (!this.applicationStateService.IsExistingLicenseApplication()) {
            return;
        }

        // The API response will return null for empty fields, where the form will have an empty string
        // if you leave the field empty after focusing out. We want to set the value to null if this is the case
        // so the comparison with the API response works correctly and it won't set the action code to modified
        // when it shouldn't.
        if (control.value === '') {
            control.patchValue(null, { emitEvent: false });
        }

        const actionCodeChanged =
            this.frequencyFormService.markActionCodeOnFrequencyChange(
                control,
                this.frequencies(),
                this.schedulesDAndHResponse!.frequencies || [],
            );

        if (actionCodeChanged) {
            setTimeout(() => {
                this.updateGrid(SchedulesDAndHGridType.frequencies);
            });
        }
    }

    /**
     * Handles the value changes of the isDeleted control for each antenna and will call the accompanying function to update the antenna accordingly.
     * @param antennas the antenna controls to wire up; must only be controls that don't already have this subscription
     */
    private markAntennaAsDeletedHandler(
        antennas: FormGroup<FormGroupType<AntennaFormModel>>[],
    ): void {
        antennas.forEach((antenna) => {
            (antenna.controls.isDeleted as FormControl).valueChanges
                .pipe(
                    takeUntil(this.destroy$),
                    tap((isDeletedValue: boolean | null) => {
                        if (isDeletedValue === null) {
                            return;
                        }

                        if (
                            this.applicationStateService.IsExistingLicenseApplication()
                        ) {
                            const actionCodeChanged =
                                this.schedulesDAndHSharedFormService.refreshFormActionCode(
                                    antenna,
                                    this.schedulesDAndHResponse!.antennas,
                                );

                            if (actionCodeChanged) {
                                this.updateGrid(
                                    SchedulesDAndHGridType.antennas,
                                );
                            }
                        }
                    }),
                )
                .subscribe();
        });
    }

    /**
     * Handles the value changes of the isDeleted control for each frequency and will call the accompanying function to update the frequency accordingly.
     * @param frequencies the frequency controls to wire up; must only be controls that don't already have this subscription
     */
    private markFrequencyAsDeletedHandler(
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
    ): void {
        frequencies.forEach((frequency) => {
            (frequency.controls.isDeleted as FormControl).valueChanges
                .pipe(
                    takeUntil(this.destroy$),
                    tap((isDeletedValue: boolean | null) => {
                        if (isDeletedValue === null) {
                            return;
                        }

                        this.schedulesDAndHSharedFormService.refreshFormActionCode(
                            frequency,
                            this.schedulesDAndHResponse!.frequencies,
                        );

                        this.updateGrid(SchedulesDAndHGridType.frequencies);
                    }),
                )
                .subscribe();
        });
    }

    private patchFormValue(response: SchedulesDAndHResponse): void {
        this.scheduleDFormService.patchFormValue(
            this.scheduleDs(),
            response.scheduleDs,
        );

        this.antennaFormService.patchFormValue(
            this.antennas(),
            response.antennas,
        );

        this.frequencyFormService.patchFormValue(
            this.frequencies(),
            response.frequencies,
        );
    }

    private getValue(): SchedulesDAndHBase {
        return {
            scheduleDs: this.scheduleDFormService.getValue(this.scheduleDs()),
            antennas: this.antennaFormService.getValue(this.antennas()),
            frequencies: this.frequencyFormService.getValue(this.frequencies()),
        };
    }

    private checkSafeHarborAndDisplayResults(): Observable<CheckSafeHarborResponse> {
        const request: CheckSafeHarborRequest = {
            applicationId: this.applicationId!,
        };

        return this.applicationService.checkSafeHarbor(request).pipe(
            takeUntil(this.destroy$),
            tap((response: CheckSafeHarborResponse) => {
                const modalData: SafeHarborResultsModalData = {
                    applicationId: this.applicationId!,
                    checkSafeHarborResponse: response,
                    schedulesDAndHFormValue: this.form
                        .value as SchedulesDAndHFormModel,
                };

                this.dialog.open(SafeHarborResultsComponent, {
                    data: modalData,
                });
            }),
        );
    }
}
